import React from 'react'
import { inject, observer } from 'mobx-react'
import _ from 'lodash'
import Icon from 'components/common/Icon'
import { trackValue } from 'utils.js'
import NumberInput from 'components/common/NumberInput'
import MapInstruction from '../common/MapInstruction'
import ColorPicker from '../common/ColorPicker'
import { withTranslation } from 'react-i18next'
import { bufferAroundPoint } from 'constants.js'
import PaneHeader from './PaneHeader'
import LocationCoordsField from './LocationCoordsField'
import CustomDropdown from '../../common/CustomDropdown'

const PointBufferPane = ({
  pointBuffer,
  group,
  uiStore,
  dataStore,
  selectionStore,
  onCancel,
  onSave,
  isQuickCreate,
  t
}) => {
  const handleEdit = () => {
    pointBuffer.initial = true
  }

  const handleConfirmAdd = async () => {
    if (!pointBuffer.validate(group)) {
      return
    }
    if (onSave) {
      onSave()
    } else if (uiStore.quickCreate && uiStore.quickCreate.isOpen) {
      uiStore.closeQuickCreate()
    }
    await uiStore.addGeopolygon(group, pointBuffer)
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
      return
    }
    if (uiStore.quickCreate && uiStore.quickCreate.isOpen) {
      uiStore.closeQuickCreate()
      return
    }
    if (pointBuffer.isUpdating) {
      selectionStore.select(
        _.find(group.geopolygons, { id: pointBuffer.id }), { data: { group } }, false, true
      )
    } else {
      selectionStore.clear()
    }
  }

  const paneTitle = pointBuffer.isUpdating
    ? t('ITEM.EDIT_BUFFER', { defaultValue: 'Edit buffer' })
    : t('ITEM.POINT_BUFFER_TITLE', { defaultValue: 'Point Buffer' })

  const submitBtnText = pointBuffer.isUpdating
    ? t('GLOBAL.CONFIRM_SAVE', { defaultValue: 'Save buffer' })
    : t('ITEM.CREATE_BUFFER_BTN', { defaultValue: 'Create point buffer' })

  if (isQuickCreate) {
    return (
      <div className='line-pane'>
        <section>
          <h2>{pointBuffer.isUpdating ? t('ITEM.EDIT_BUFFER', { defaultValue: 'Edit buffer' }) : t('ITEM.CREATE_BUFFER', { defaultValue: 'Create buffer' })}</h2>
        </section>
        <section>
          {
            pointBuffer.position
              ? null
              : <MapInstruction>{t('ITEM.POINT_START')}</MapInstruction>
          }
        </section>
        <form>
          <section className='spaced-children'>
            <label>
              <span>{t('GLOBAL.NAME')}</span>
              <input type='text' {...trackValue(pointBuffer, 'name')} autoFocus />
              <span className='invalid'>{pointBuffer.invalid.get('name')}</span>
            </label>
            <label>
              <span>{t('GLOBAL.LONGITUDE')}</span>
              <NumberInput
                min={-180}
                max={180}
                value={pointBuffer.position.longitude}
                inputValue={pointBuffer.position.longitude}
                onChange={value => (pointBuffer.position.longitude = value)}
              />
              <span className='invalid'>{pointBuffer.invalid.get('longitude')}</span>
            </label>
            <label>
              <span>{t('GLOBAL.LATITUDE')}</span>
              <NumberInput
                min={-90}
                max={90}
                value={pointBuffer.position.latitude}
                inputValue={pointBuffer.position.latitude}
                onChange={value => (pointBuffer.position.latitude = value)}
              />
              <span className='invalid'>{pointBuffer.invalid.get('latitude')}</span>
            </label>
            <label>
              <span>{t('GLOBAL.BUFFER')}</span>
              <div className='input-with-unit'>
                <NumberInput
                  min={1}
                  max={1000}
                  inputValue={pointBuffer.buffer}
                  value={pointBuffer.buffer}
                  onChange={value => (pointBuffer.buffer = value)}
                />
                <CustomDropdown
                  className='custom-dropdown--unit'
                  value={pointBuffer.bufferUnit}
                  options={[
                    { value: 'km', label: t('GLOBAL.UNIT_KM', { defaultValue: 'km' }) },
                    { value: 'mile', label: t('GLOBAL.UNIT_MILE', { defaultValue: 'mile' }) }
                  ]}
                  onChange={value => (pointBuffer.bufferUnit = value)}
                />
              </div>
              <span className='invalid'>{pointBuffer.invalid.get('buffer')}</span>
            </label>
            {
              !pointBuffer.isUpdating && (
                <div>
                  <ColorPicker
                    onClick={(idx) => { pointBuffer.color_id = idx }}
                    selectedId={pointBuffer.color_id}
                    hasTransparencyOption
                    transparency={pointBuffer.transparency}
                    onTransparencyChange={(val) => { pointBuffer.transparency = val }}
                  />
                </div>
              )
            }
          </section>
          <section>
            <div className='centered-button-row'>
              <button
                type='button'
                onClick={handleCancel}
              >
                <Icon id='fa-times' />
                <span>{t('GLOBAL.CANCEL')}</span>
              </button>
              <button
                type='button'
                onClick={() => pointBuffer.isUpdating
                  ? uiStore.updateGeopolygon(group, pointBuffer)
                  : handleConfirmAdd()}
              >
                <Icon id='fa-check' />
                <span>{pointBuffer.isUpdating ? t('GLOBAL.CONFIRM_SAVE') : t('GLOBAL.CONFIRM_ADD')}</span>
              </button>
            </div>
          </section>
        </form>
      </div>
    )
  }

  return (
    <div className='line-pane'>
      <PaneHeader
        icon={bufferAroundPoint}
        title={paneTitle}
        onClose={handleCancel}
      />

      {(!pointBuffer.isUpdating && (!pointBuffer.position.latitude || !pointBuffer.position.longitude)) && (
        <MapInstruction>{t('ITEM.POINT_START', { defaultValue: 'Click on the map to place the point' })}</MapInstruction>
      )}

      <form onSubmit={(e) => {
        e.preventDefault()
        if (pointBuffer.isUpdating) {
          uiStore.updateGeopolygon(group, pointBuffer)
        } else {
          handleConfirmAdd()
        }
      }}
      >
        <section className='spaced-children'>
          <label>
            <span>{t('GLOBAL.NAME', { defaultValue: 'Name' })}</span>
            <input
              type='text'
              placeholder={t('ITEM.BUFFER_NAME_PLACEHOLDER', { defaultValue: 'e.g. 5km radius buffer' })}
              {...trackValue(pointBuffer, 'name')}
              autoFocus
            />
            <span className='invalid'>{pointBuffer.invalid.get('name')}</span>
          </label>

          <LocationCoordsField
            position={pointBuffer.position}
            onLatitudeChange={value => {
              pointBuffer.position.latitude = value
              pointBuffer.initial = false
            }}
            onLongitudeChange={value => {
              pointBuffer.position.longitude = value
              pointBuffer.initial = false
            }}
            invalidLatitude={pointBuffer.invalid.get('latitude')}
            invalidLongitude={pointBuffer.invalid.get('longitude')}
            t={t}
          />

          <label>
            <span>{t('GLOBAL.BUFFER_RADIUS', { defaultValue: 'Buffer radius' })}</span>
            <div className='input-with-unit'>
              <NumberInput
                min={1}
                max={1000}
                inputValue={pointBuffer.buffer}
                value={pointBuffer.buffer}
                onChange={value => (pointBuffer.buffer = value)}
              />
              <CustomDropdown
                className='custom-dropdown--unit'
                value={pointBuffer.bufferUnit}
                options={[
                  { value: 'km', label: t('GLOBAL.UNIT_KM', { defaultValue: 'km' }) },
                  { value: 'mile', label: t('GLOBAL.UNIT_MILE', { defaultValue: 'mile' }) }
                ]}
                onChange={value => (pointBuffer.bufferUnit = value)}
              />
            </div>
            <span className='invalid'>{pointBuffer.invalid.get('buffer')}</span>
          </label>

          {
            !pointBuffer.isUpdating && (
              <div className='line-pane__picker'>
                <ColorPicker
                  onClick={(idx) => { pointBuffer.color_id = idx }}
                  selectedId={pointBuffer.color_id}
                  hasTransparencyOption
                  transparency={pointBuffer.transparency}
                  onTransparencyChange={(val) => { pointBuffer.transparency = val }}
                />
              </div>
            )
          }
        </section>

        {
          !pointBuffer.initial && (
            <div>
              <button
                type='button'
                className='finish-line edit-buffer-button'
                onClick={handleEdit}
              >
                <span>{t('ITEM.EDIT_POINT')}</span>
              </button>
            </div>
          )
        }

        <section>
          <div className='centered-button-row'>
            <button
              type='button'
              onClick={handleCancel}
            >
              <span>{t('GLOBAL.CANCEL', { defaultValue: 'Cancel' })}</span>
            </button>
            <button
              type='button'
              onClick={() => pointBuffer.isUpdating
                ? uiStore.updateGeopolygon(group, pointBuffer)
                : handleConfirmAdd()}
            >
              <span>{submitBtnText}</span>
            </button>
          </div>
        </section>
      </form>
    </div>
  )
}

export default withTranslation()(inject('uiStore', 'dataStore', 'selectionStore')(observer(PointBufferPane)))
